<template>
	<div v-if="numSundays || numSaturdays || numWeekdays" class="pb-5 mt-5 text-left pl-2">
		<div class="black-text font-weight-black black--text mt-4 mb-2" style="font-size: 1rem">{{ getLabel('bookingFlowBookingSummaryTitle', 'Booking Summary') }}</div>
		<div style="font-size: 13px; color: #3e3e3e">
			<div v-if="numWeekdays">
				<strong> Weekday Rate x {{ numWeekdays }}:</strong>
				<span class="ml-2">R{{ ratesForWeekdays.toLocaleString() }}</span>
			</div>
			<div v-if="numSaturdays">
				<strong>Saturday Rate x {{ numSaturdays }}:</strong>
				<span class="ml-2">R{{ ratesForSaturdays.toLocaleString() }}</span>
			</div>
			<div v-if="numSundays">
				<strong>Sunday/Public Holiday Rate x {{ numSundays }}:</strong>
				<span class="ml-2">R{{ ratesForSundays.toLocaleString() }}</span>
			</div>
			<div v-if="lunchDetails.text">
				<strong>Lunch:</strong>
				<span class="ml-2"
					>{{ lunchDetails.text }} <span v-if="lunchDetails.showCost">{{ `(less R${totalLunch})` }}</span></span
				>
			</div>
			<hr class="mt-3 mb-3" style="background: #f0f0f0; width: 90%; border: none; height: 2px" />
			<div>
				<strong>{{ getLabel('bookingFlowBookingSummaryTotalTitle', 'Total Estimated Locum Fee') }}:</strong>
				<span class="ml-2">R{{ total }}</span>
			</div>
		</div>
		<div>
			<small>*Excludes <a href="https://locumbase.com/pricing" target="_blank">Facilitation Fee</a> </small>
		</div>
	</div>
</template>

<script>
import store from '@/store';
import moment from 'moment';

export default {
	name: 'BookingCostSummary',
	props: {
		dates: Array,
		locum: Object,
		booking: Object,
	},
	data: () => ({
		defaultSummary: {
			weekdays: {
				days: 0,
				total: 0,
				lunchTotal: 0,
			},
			saturday: {
				days: 0,
				total: 0,
				lunchTotal: 0,
			},
			sundaysAndHolidays: {
				days: 0,
				total: 0,
				lunchTotal: 0,
			},
		},
		bookingSummary: {},
	}),
	computed: {
		total() {
			const totalCost = this.ratesForSundays + this.ratesForSaturdays + this.ratesForWeekdays - this.totalLunch;
			return totalCost.toLocaleString();
		},
		totalLunch() {
			const totalCost = this.bookingSummary.weekdays.lunchTotal + this.bookingSummary.saturday.lunchTotal + this.bookingSummary.sundaysAndHolidays.lunchTotal;
			return totalCost.toLocaleString();
		},
		numWeekdays() {
			return this.bookingSummary['weekdays']?.days;
		},
		numSaturdays() {
			return this.bookingSummary['saturday']?.days;
		},
		numSundays() {
			return this.bookingSummary['sundaysAndHolidays']?.days;
		},
		ratesForWeekdays() {
			return this.bookingSummary['weekdays']?.total;
		},
		ratesForSaturdays() {
			return this.bookingSummary['saturday']?.total;
		},
		ratesForSundays() {
			return this.bookingSummary['sundaysAndHolidays']?.total;
		},
		lunchDetails() {
			if (!this.booking || !this.booking.lunch) {
				return {
					showCost: false,
					hasLunch: false,
					text: '',
				};
			}
			const { allowed, paid, period } = this.booking.lunch;

			if (!allowed) {
				return {
					showCost: false,
					hasLunch: false,
					text: 'No Lunch',
				};
			}

			const timePeriod = period === '0.5' ? '30 minutes' : '1 hour';

			return paid
				? {
						showCost: false,
						hasLunch: true,
						text: `Paid ${timePeriod}`,
				  }
				: {
						showCost: true,
						hasLunch: false,
						text: `Unpaid ${timePeriod}`,
				  };
		},
	},
	watch: {
		dates() {
			this.checkDates();
		},
		locum() {
			this.checkDates();
		},
		booking() {
			this.checkDates();
		},
	},
	mounted() {
		this.checkDates();
	},
	methods: {
		checkDates() {
			this.bookingSummary = JSON.parse(JSON.stringify(this.defaultSummary));
			let datesForSummary = this.dates;

			//For old start and end time
			if (this.booking && this.booking.booking_dates) {
				const { start_time, end_time, booking_dates } = this.booking;
				datesForSummary = booking_dates.map(bookingDate => ({ date: bookingDate, start_time, end_time }));
			}

			//Use request_dates' times because it is accurate. It will overwrite old start and end time
			if (this.booking && this.booking.request_dates && this.booking.request_dates.length > 0) {
				const validDates = [];
				for (let index = 0; index < datesForSummary.length; index++) {
					let request_day = this.booking.request_dates.find(date => date.date == datesForSummary[index].date);
					if (!request_day) continue;

					datesForSummary[index].start_time = moment(request_day.start_time).format('HH:mm');
					datesForSummary[index].end_time = moment(request_day.end_time).format('HH:mm');
					validDates.push(datesForSummary[index]);
				}

				datesForSummary = validDates;
			}

			if (datesForSummary && this.locum) {
				for (let dateObject of datesForSummary) {
					this.checkBookingSummary(dateObject);
				}
			}
		},
		hoursBetween(a, b) {
			return Math.fround(
				moment()
					.add(moment.duration(b))
					.diff(moment().add(moment.duration(a)), 'hours', true)
			).toFixed();
		},
		getCostOfDay(dateInfo) {
			const { start_time, end_time } = dateInfo;
			const dayHours = this.hoursBetween(start_time, end_time);

			const hoursIncLunch = dayHours;

			const type = this.getDayType(dateInfo);

			return this.getDayRate(type) * hoursIncLunch;
		},
		getLunchHours() {
			const lunchSource = this.booking ? this.booking : store.getters.getPracticeOffer;
			if (!lunchSource) {
				return 0;
			}
			const { allowed, paid, period } = lunchSource?.lunch;
			if (!allowed || paid) {
				return 0;
			}
			return period;
		},
		checkBookingSummary(dateInfo) {
			const dayTotal = this.getCostOfDay(dateInfo);
			const type = this.getDayType(dateInfo);
			const { days, total, lunchTotal } = this.bookingSummary[type];

			this.bookingSummary[type] = {
				days: days + 1,
				total: total + dayTotal,
				lunchTotal: lunchTotal + this.getDayRate(type) * this.getLunchHours(),
			};
		},
		getDayType(dateInfo) {
			const public_holidays = store.getters.getPublicHolidays || [];
			const { date } = dateInfo;

			const [year, month, day] = date.split('-');
			const formattedDate = `${year}-${day}-${month}`;

			const dayOfWeek = moment(date).day();
			const publicHolidayRate = public_holidays.includes(formattedDate);

			if (publicHolidayRate || dayOfWeek === 0) {
				return 'sundaysAndHolidays';
			} else {
				if (dayOfWeek === 6) {
					return 'saturday';
				} else {
					return 'weekdays';
				}
			}
		},
		getDayRate(type) {
			const { working_hours } = this.locum;
			const typeFinal = type === 'sundaysAndHolidays' ? 'sundays' : type;
			return working_hours ? Number(working_hours[typeFinal].rate) : 0;
		},
	},
};
</script>
